import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/use-auth'
import { useOnboarding } from '@/hooks/use-onboarding'
import { Card } from '@/components/ui/card'
import { cn } from '@/lib/utils'
import { CURRENT_CLIENT } from '@/constants'
import {
  countedOnboardingSteps,
  employerAccountOnboardingSteps,
  onboardingDismissalStorageKey,
  onboardingProgressSummary,
  shouldBackfillEmployerOrganisationSetup,
} from '@/lib/onboarding-display'
import { onboardingWelcomeHeaderFromSteps } from '@/lib/onboarding-setup-list-copy'
import { OnboardingSetupCardSkeleton } from '@/components/onboarding/onboarding-setup-card-skeleton'
import { OnboardingWelcomePanel } from '@/components/onboarding/onboarding-welcome-panel'
import { OnboardingCompleteCard } from '@/components/onboarding/onboarding-complete-card'
import { beginOnboardingFromLauncher } from '@/lib/begin-onboarding'

interface OnboardingSetupCardProps {
  className?: string
  onAskAgent?: (message: string) => void
}

const DEFAULT_ASK_AGENT_MESSAGE = "What's left in my setup?"

export function OnboardingSetupCard({ className, onAskAgent }: OnboardingSetupCardProps) {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { status, isLoading, completeStep } = useOnboarding()
  const [continuing, setContinuing] = useState(false)

  useEffect(() => {
    if (!user?.email || !status?.group_name || status.is_completed) return
    if (status.has_onboarding) {
      localStorage.removeItem(onboardingDismissalStorageKey(user.email, status.group_name))
    }
  }, [user?.email, status?.group_name, status?.has_onboarding, status?.is_completed])

  useEffect(() => {
    if (!shouldBackfillEmployerOrganisationSetup(status)) return
    void completeStep('organisation_setup')
  }, [completeStep, status])

  if (isLoading) {
    return (
      <div className={className}>
        <OnboardingSetupCardSkeleton />
      </div>
    )
  }

  if (!status || !status.has_onboarding) {
    return null
  }

  if (status.is_completed) {
    return <OnboardingCompleteCard groupName={status.group_name} className={className} />
  }

  const steps = status.steps || []
  const accountSteps = employerAccountOnboardingSteps(steps, status.group_name)
  const countedSteps = countedOnboardingSteps(accountSteps)
  if (countedSteps.length === 0) {
    return null
  }

  const completedIds = status.completed_step_ids ?? []
  const progressSummary = onboardingProgressSummary(accountSteps, completedIds)
  const welcomeHeader = onboardingWelcomeHeaderFromSteps(steps)
  const tagline = CURRENT_CLIENT.tagline?.trim()
  const clientName = CURRENT_CLIENT.name || 'Yes Human'
  const title = welcomeHeader.title.replace('{client_name}', clientName)

  const handleContinue = async () => {
    setContinuing(true)
    try {
      await beginOnboardingFromLauncher(status, completeStep, navigate)
    } finally {
      setContinuing(false)
    }
  }

  return (
    <Card className={cn('shadow-sm', className)}>
      <div className="p-[var(--sp-card)]">
        <OnboardingWelcomePanel
          title={title}
          description={welcomeHeader.description}
          tagline={tagline}
          coPilotHint="Continue in the wizard, or ask the agent to help with any step."
          completed={progressSummary.completed}
          total={progressSummary.total}
          percent={progressSummary.percent}
          onContinue={handleContinue}
          isContinuing={continuing}
          onAskAgent={
            onAskAgent
              ? () => onAskAgent(DEFAULT_ASK_AGENT_MESSAGE)
              : undefined
          }
        />
      </div>
    </Card>
  )
}
